/**
 * Admin Panel Type Definitions
 *
 * スキルチェック精度管理ツールのフロントエンド型定義
 * バックエンドのレスポンスDTOと一致するように定義
 */

// ========================================
// 基本型定義（Enums & Literals）
// ========================================

/**
 * スキルチェックジョブのステータス
 * @source supabase/functions/api-v1/presentation/admin/admin.response.dto.ts
 */
export type SkillCheckJobStatus =
  | "PENDING"
  | "PROCESSING"
  | "COMPLETED"
  | "FAILED"
  | "CANCELLED"
  | "RETRYABLE";

/**
 * マッチレベル（1-5）
 * ※ DBスキーマでは歴史的理由で0-5を許容するが、実際の計算結果は常に1-5の範囲
 * ※ フィルタリングでは0も許容（レガシーデータ対応）
 * @source .kiro/specs/skill-check-admin-tool/requirements.md
 */
export type MatchLevel = 1 | 2 | 3 | 4 | 5;

/**
 * エラーコード
 * リトライ可能なエラーと不可なエラーを分類
 * @source .kiro/specs/skill-check-admin-tool/data-model.md
 */
export type ErrorCode =
  | "AI_TIMEOUT_ERROR" // AI処理タイムアウト（リトライ可）
  | "NETWORK_ERROR" // ネットワークエラー（リトライ可）
  | "DATABASE_ERROR" // データベースエラー（リトライ可）
  | "VALIDATION_ERROR" // バリデーションエラー（リトライ不可）
  | "AUTHORIZATION_ERROR" // 認可エラー（リトライ不可）
  | "DATA_INTEGRITY_ERROR" // データ整合性エラー（リトライ不可）
  | "HTML_SIZE_EXCEEDED" // HTMLサイズ超過（リトライ不可）
  | "PROCESSING_TIMEOUT"; // 処理タイムアウト（リトライ可）

/**
 * ステップ名（AI処理の各段階）
 * @source supabase/functions/api-v1/presentation/admin/admin.response.dto.ts
 */
export type StepName =
  | "candidate_extraction" // Step 1: 候補者情報抽出
  | "skill_matching" // Step 2: スキルマッチング分析
  | "criteria_evaluation"; // Step 3: カスタム判定評価

/**
 * ステップステータス
 * @source supabase/functions/api-v1/presentation/admin/admin.response.dto.ts
 */
export type StepStatus = "in_progress" | "completed" | "failed";

/**
 * カスタム判定結果
 * @source supabase/functions/api-v1/presentation/admin/admin.response.dto.ts
 */
export type CriteriaResult =
  | "perfect" // 完全にマッチ（100%）
  | "good" // ほぼマッチ（75%）
  | "partial" // 部分的にマッチ（50%）
  | "mismatch" // マッチしない（0%）
  | "unknown"; // 判定不能（計算から除外）

// ========================================
// 候補者情報
// ========================================

/**
 * 候補者情報（LLM抽出結果）
 * Step 1: candidate_extraction の出力データ構造
 * @source .kiro/specs/skill-check-admin-tool/data-model.md
 */
export type CandidateInfo = {
  /** 候補者名 */
  name?: string;
  /** 現職 */
  currentPosition?: string;
  /** 現在の勤務先会社名 */
  company?: string | null;
  /** 経歴サマリー */
  experience?: string;
  /** スキルリスト */
  skills?: string[];
  /** 学歴 */
  education?: string;
  /** プロフィールサマリー */
  profileSummary?: string;
  /** 年齢 */
  age?: number | null;
  /** 性別 */
  gender?: string | null;
  /** 現住所 */
  currentAddress?: string | null;
  /** 直近の年収 */
  currentSalary?: string | null;
  /** 希望年収 */
  desiredSalary?: string | null;
  /** 外部プロフィールURL */
  profileUrl?: string | null;
  /** 求人サイト名 */
  source?: string | null;
};

// ========================================
// スキルチェック一覧
// ========================================

/**
 * スキルチェック一覧アイテム
 * @source supabase/functions/api-v1/presentation/admin/admin.response.dto.ts - SkillCheckAdminItemResponse
 */
export type SkillCheckAdminItem = {
  /** ジョブID */
  id: string;
  /** ステータス */
  status: SkillCheckJobStatus;
  /** 候補者名（DOM抽出またはAI抽出） */
  candidateName: string | null;
  /** 候補者ソース（取得元サイト名） */
  candidateSource: string | null;
  /** 候補者ページURL */
  pageUrl: string;
  /** 求人ID */
  jobPostingId: string;
  /** 求人タイトル */
  jobPostingTitle: string;
  /** チームID */
  teamId: string;
  /** チーム名 */
  teamName: string;
  /** 会社ID */
  companyId: string;
  /** 会社名 */
  companyName: string;
  /** 作成日時（ISO8601形式） */
  createdAt: string;
  /** 処理開始日時（ISO8601形式） */
  startedAt: string | null;
  /** 処理完了日時（ISO8601形式） */
  completedAt: string | null;
  /** マッチレベル（1-5、完了時のみ） */
  overallMatchLevel: number | null;
  /** エラーコード（失敗時のみ） */
  errorCode: string | null;
  /** エラーメッセージ（失敗時のみ） */
  errorMessage: string | null;
  /** リトライ回数 */
  retryCount: number;
};

// ========================================
// スキルチェック詳細
// ========================================

/**
 * 部分マッチスキル
 * @source supabase/functions/api-v1/presentation/admin/admin.response.dto.ts - PartialMatchSkillResponse
 */
export type PartialMatchSkill = {
  /** スキル名 */
  skill: string;
  /** マッチレベル */
  matchLevel: number;
  /** コメント（部分マッチの理由） */
  comment: string;
};

/**
 * カスタム判定結果アイテム
 * @source supabase/functions/api-v1/presentation/admin/admin.response.dto.ts - CriteriaResultItemResponse
 */
export type CriteriaResultItem = {
  /** 結果ID */
  id: string;
  /** 判定項目ID */
  criteriaId: string;
  /** 判定項目テキスト */
  criteriaText: string;
  /** 重要度 */
  importance: string;
  /** 判定結果 */
  result: CriteriaResult;
  /** 判定理由 */
  reason: string;
};

/**
 * スキルチェック詳細
 * @source supabase/functions/api-v1/presentation/admin/admin.response.dto.ts - SkillCheckDetailAdminResponse
 */
export interface SkillCheckJobDetail extends SkillCheckAdminItem {
  /** HTMLコンテンツ（オプション、サイズ制限により省略される場合あり） */
  htmlContent?: string;
  /** スキルチェック結果ID */
  skillCheckResultId: string | null;
  /** マッチした必須スキル */
  matchedRequiredSkills: string[];
  /** マッチした歓迎スキル */
  matchedPreferredSkills: string[];
  /** 不足必須スキル */
  missingRequiredSkills: string[];
  /** 不足歓迎スキル */
  missingPreferredSkills: string[];
  /** 部分マッチスキル */
  partialMatchSkills: PartialMatchSkill[];
  /** 評価コメント */
  evaluationComment: string | null;
  /** カスタム判定結果 */
  criteriaResults: CriteriaResultItem[];
}

// ========================================
// ジョブステップ詳細（LLM入出力）
// ========================================

/**
 * スキルチェックジョブステップ詳細
 * LLMの入出力データを含む
 * @source supabase/functions/api-v1/presentation/admin/admin.response.dto.ts - SkillCheckJobStepDetailResponse
 */
export type SkillCheckJobStepDetail = {
  /** ステップID */
  id: string;
  /** ジョブID */
  jobId: string;
  /** ステップ番号（1, 2, 3） */
  stepNumber: 1 | 2 | 3;
  /** ステップ名 */
  stepName: StepName;
  /** ステップステータス */
  status: StepStatus;
  /** LLMへの入力データ */
  inputData: Record<string, unknown> | null;
  /** LLMからの出力データ */
  outputData: Record<string, unknown> | null;
  /** エラーメッセージ（失敗時のみ） */
  errorMessage: string | null;
  /** 処理開始日時（ISO8601形式） */
  startedAt: string;
  /** 処理完了日時（ISO8601形式） */
  completedAt: string | null;
};

// ========================================
// スコアリング内訳
// ========================================

/**
 * コンテンツベースのスコア内訳
 * @source supabase/functions/api-v1/presentation/admin/admin.response.dto.ts - ContentBasedScoreResponse
 */
export type ContentBasedScore = {
  /** 必須スキルマッチ率（0.0-1.0） */
  requiredSkillsMatchRate: number;
  /** 歓迎スキルマッチ率（0.0-1.0） */
  preferredSkillsMatchRate: number;
  /** 経験マッチ率（0.0-1.0） */
  experienceMatchRate: number;
  /** 重み付けスコア（0.0-1.0） */
  weightedScore: number;
};

/**
 * カスタム判定スコア結果項目
 * @source supabase/functions/api-v1/presentation/admin/admin.response.dto.ts - CriteriaScoreResultResponse
 */
export type CriteriaScoreResult = {
  /** 判定項目テキスト */
  criteriaText: string;
  /** 重要度 */
  importance: string;
  /** 判定結果 */
  result: CriteriaResult;
  /** 判定理由 */
  reason: string;
};

/**
 * カスタム判定スコア
 * @source supabase/functions/api-v1/presentation/admin/admin.response.dto.ts - CriteriaScoreResponse
 */
export type CriteriaScore = {
  /** 重み付けスコア（0.0-1.0） */
  weightedScore: number;
  /** レベル制限（制限がなければnull） */
  levelRestriction: number | null;
  /** 制限理由 */
  restrictionReason: string | null;
  /** 各判定項目の結果 */
  results: CriteriaScoreResult[];
};

/**
 * 最終スコア
 * @source supabase/functions/api-v1/presentation/admin/admin.response.dto.ts - FinalScoreResponse
 */
export type FinalScore = {
  /** 重み付けスコア（0.0-1.0） */
  weightedScore: number;
  /** 計算されたレベル（制限前） */
  calculatedLevel: number;
  /** 最終レベル（制限適用後） */
  restrictedLevel: number;
  /** 計算式 */
  formula: string;
};

/**
 * スコアリング内訳
 * @source supabase/functions/api-v1/presentation/admin/admin.response.dto.ts - ScoringBreakdownAdminResponse
 */
export type ScoringBreakdown = {
  /** スコアリングロジックのバージョン */
  version: string;
  /** コンテンツベーススコア */
  contentBasedScore: ContentBasedScore;
  /** カスタム判定スコア（null = カスタム判定なし、コンテンツベース100%） */
  criteriaScore: CriteriaScore | null;
  /** 最終スコア */
  finalScore: FinalScore;
};

// ========================================
// 統計情報
// ========================================

/**
 * マッチレベル分布項目
 * @source supabase/functions/api-v1/presentation/admin/admin.response.dto.ts - MatchLevelDistributionItem
 */
export type MatchLevelDistributionItem = {
  /** マッチレベル（1-5） */
  level: number;
  /** 件数 */
  count: number;
};

/**
 * エラーコード分布項目
 * @source supabase/functions/api-v1/presentation/admin/admin.response.dto.ts - ErrorCodeDistributionItem
 */
export type ErrorCodeDistributionItem = {
  /** エラーコード */
  code: string;
  /** 件数 */
  count: number;
};

/**
 * 統計情報
 * @source supabase/functions/api-v1/presentation/admin/admin.response.dto.ts - SkillCheckStatsAdminResponse
 */
export type SkillCheckStats = {
  /** 総スキルチェック数 */
  total: number;
  /** 完了数 */
  completed: number;
  /** 失敗数 */
  failed: number;
  /** 待機中数 */
  pending: number;
  /** 成功率（0.0-1.0） */
  successRate: number;
  /** 平均処理時間（ミリ秒） */
  averageProcessingTimeMs: number;
  /** マッチレベル分布 */
  matchLevelDistribution: MatchLevelDistributionItem[];
  /** エラーコード分布 */
  errorCodeDistribution: ErrorCodeDistributionItem[];
};

// ========================================
// エラー分析
// ========================================

/**
 * エラー分析アイテム
 * @source supabase/functions/api-v1/presentation/admin/admin.response.dto.ts - ErrorAnalysisItemResponse
 */
export type ErrorAnalysisItem = {
  /** ジョブID */
  id: string;
  /** エラーコード */
  errorCode: string;
  /** エラーメッセージ */
  errorMessage: string;
  /** 失敗したステップ名 */
  stepName: StepName | null;
  /** 作成日時（ISO8601形式） */
  createdAt: string;
  /** 候補者名 */
  candidateName: string | null;
  /** 求人タイトル */
  jobPostingTitle: string;
  /** チーム名 */
  teamName: string;
  /** 会社名 */
  companyName: string;
};

/**
 * エラーコード別サマリー項目
 * @source supabase/functions/api-v1/presentation/admin/admin.response.dto.ts - ErrorCodeSummaryItem
 */
export type ErrorCodeSummaryItem = {
  /** エラーコード */
  code: string;
  /** 件数 */
  count: number;
  /** 割合（0.0-1.0） */
  percentage: number;
};

/**
 * ステップ別サマリー項目
 * @source supabase/functions/api-v1/presentation/admin/admin.response.dto.ts - StepSummaryItem
 */
export type StepSummaryItem = {
  /** ステップ名 */
  step: string;
  /** 件数 */
  count: number;
};

/**
 * エラー分析サマリー
 * @source supabase/functions/api-v1/presentation/admin/admin.response.dto.ts - ErrorAnalysisSummaryResponse
 */
export type ErrorAnalysisSummary = {
  /** 総エラー数 */
  totalErrors: number;
  /** エラーコード別集計 */
  byErrorCode: ErrorCodeSummaryItem[];
  /** ステップ別集計 */
  byStep: StepSummaryItem[];
};

// ========================================
// フィルタ関連
// ========================================

/**
 * フィルタ選択肢（チーム）
 * @source supabase/functions/api-v1/presentation/admin/admin.response.dto.ts
 */
export type FilterTeamOption = {
  /** チームID */
  id: string;
  /** チーム名 */
  name: string;
  /** 会社ID */
  companyId: string;
};

/**
 * フィルタ選択肢（会社）
 * @source supabase/functions/api-v1/presentation/admin/admin.response.dto.ts
 */
export type FilterCompanyOption = {
  /** 会社ID */
  id: string;
  /** 会社名 */
  name: string;
};

/**
 * フィルタ選択肢（求人）
 * @source supabase/functions/api-v1/presentation/admin/admin.response.dto.ts
 */
export type FilterJobPostingOption = {
  /** 求人ID */
  id: string;
  /** 求人タイトル */
  title: string;
  /** チームID */
  teamId: string;
};

/**
 * フィルタ選択肢
 * GET /api-v1/admin/filter-options のレスポンス型
 * @source .kiro/specs/skill-check-admin-tool/design.md
 */
export type FilterOptions = {
  /** チーム一覧 */
  teams: FilterTeamOption[];
  /** 会社一覧 */
  companies: FilterCompanyOption[];
  /** 求人一覧 */
  jobPostings: FilterJobPostingOption[];
};

/**
 * スキルチェック一覧フィルタ条件
 * @source .kiro/specs/skill-check-admin-tool/design.md
 */
export type SkillCheckFilters = {
  /** ステータスフィルタ */
  status?: SkillCheckJobStatus;
  /** マッチレベルフィルタ（1-5、UI側では数値として扱われる） */
  matchLevel?: number;
  /** 開始日時（ISO8601形式） */
  since?: string;
  /** 終了日時（ISO8601形式） */
  until?: string;
  /** チームIDフィルタ */
  teamId?: string;
  /** 会社IDフィルタ */
  companyId?: string;
  /** 求人IDフィルタ */
  jobPostingId?: string;
  /** 検索クエリ（候補者名・求人タイトル）（LIST-007） */
  query?: string;
  /** 作成日時ソート順（LIST-008） */
  sortOrder?: "asc" | "desc";
};

// ========================================
// APIレスポンス共通
// ========================================

/**
 * ページネーション付きレスポンス
 */
export type PaginatedResponse<T> = {
  /** アイテム一覧 */
  items: T[];
  /** 総件数 */
  total: number;
  /** さらにデータがあるか */
  hasMore: boolean;
};

/**
 * スキルチェック一覧レスポンス
 * @source supabase/functions/api-v1/presentation/admin/admin.response.dto.ts - ListSkillChecksAdminResponse
 */
export type ListSkillChecksResponse = PaginatedResponse<SkillCheckAdminItem>;

/**
 * ステップ詳細レスポンス
 * @source supabase/functions/api-v1/presentation/admin/admin.response.dto.ts - GetSkillCheckStepsAdminResponse
 */
export type GetSkillCheckStepsResponse = {
  /** ステップ一覧 */
  steps: SkillCheckJobStepDetail[];
};

/**
 * エラー分析レスポンス
 * @source supabase/functions/api-v1/presentation/admin/admin.response.dto.ts - ErrorAnalysisAdminResponse
 */
export type ErrorAnalysisResponse = {
  /** エラー一覧 */
  items: ErrorAnalysisItem[];
  /** 総件数 */
  total: number;
  /** さらにデータがあるか */
  hasMore: boolean;
  /** サマリー */
  summary: ErrorAnalysisSummary;
};

// ========================================
// ユーティリティ型
// ========================================

/**
 * エラーコードがリトライ可能かどうかを判定するためのマッピング
 */
export const RETRYABLE_ERROR_CODES: ErrorCode[] = [
  "AI_TIMEOUT_ERROR",
  "NETWORK_ERROR",
  "DATABASE_ERROR",
  "PROCESSING_TIMEOUT",
];

/**
 * エラーコードがリトライ不可かどうかを判定するためのマッピング
 */
export const NON_RETRYABLE_ERROR_CODES: ErrorCode[] = [
  "VALIDATION_ERROR",
  "AUTHORIZATION_ERROR",
  "DATA_INTEGRITY_ERROR",
  "HTML_SIZE_EXCEEDED",
];

/**
 * ステップ番号からステップ名へのマッピング
 */
export const STEP_NUMBER_TO_NAME: Record<1 | 2 | 3, StepName> = {
  1: "candidate_extraction",
  2: "skill_matching",
  3: "criteria_evaluation",
};

/**
 * ステップ名から表示名へのマッピング
 */
export const STEP_DISPLAY_NAMES: Record<StepName, string> = {
  candidate_extraction: "候補者情報抽出",
  skill_matching: "スキルマッチング分析",
  criteria_evaluation: "カスタム判定評価",
};

/**
 * ステータスから表示名へのマッピング
 */
export const STATUS_DISPLAY_NAMES: Record<SkillCheckJobStatus, string> = {
  PENDING: "待機中",
  PROCESSING: "処理中",
  COMPLETED: "完了",
  FAILED: "失敗",
  RETRYABLE: "リトライ待ち",
  CANCELLED: "キャンセル",
};

/**
 * 判定結果から表示名へのマッピング
 */
export const CRITERIA_RESULT_DISPLAY_NAMES: Record<CriteriaResult, string> = {
  perfect: "完全マッチ",
  good: "ほぼマッチ",
  partial: "部分マッチ",
  mismatch: "マッチなし",
  unknown: "判定不能",
};

// ========================================
// 後方互換性のためのエイリアス
// ========================================

/**
 * @deprecated SkillCheckJobDetail を使用してください
 */
export type SkillCheckAdminDetail = SkillCheckJobDetail;
